import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import { useToast } from "../../../hooks/toast";
import * as Yup from "yup";
import getValidationErrors from "../../../utils/getValidationErrors";
import api from "../../../service";
import { useCallback } from "react";
import CpfCnpj from "../../../components/CpfCnpj";
import { Form } from "@unform/web";
import { validateSingleFieldOnBlur } from "../../../utils/formValidations";
import ModalWithCheckbox from "../../../components/ModalWithCheckbox";

import {
  Container,
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  LinkButton,
  GoBackContainer,
  BadgeContainer,
  UserNameContainer,
} from "./styles";
import SelectWithLabel from "../../../components/SelectWithLabel";
import Badge from "../../../components/Badge";
import { useLoading } from "../../../hooks/loading";
import Toggle from "../../../components/Toggle";
import useProvisionTypes from "../../../hooks/useProvisionTypes";

const CreateAssociation = () => {
  const { token } = useSelector((state) => state.auth);
  const [allEvents, setAllEvents] = useState([]);
  const [service, setService] = useState();
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [serviceProvider, setServiceProvider] = useState(null);
  const [event, setEvent] = useState();
  const [contract, setContract] = useState();
  const formRef = useRef(null);
  const associationFormRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();
  const [loading, setLoading] = useState(false);

  const schema = Yup.object().shape({
    event: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    contract: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    document: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
  });

  const { provisionTypes } = useProvisionTypes();

  const schemaAssociationForm = Yup.object().shape({
    service_type: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
  });

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        await schema.validate(data, { abortEarly: false });

        setLoading(true);
        if (loading) {
          return;
        }

        setServiceProvider(null);
        clearFormErrors(associationFormRef);

        associationFormRef.current?.setFieldValue("service_type", []);

        api
          .get(`/provider-association/service-provider/${data.document}`, {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          })
          .then((result) => {
            let translatedServices = [];

            result.data.output.services.forEach((item) => {
              provisionTypes.forEach((s) => {
                if (s.value === item.value) {
                  translatedServices.push({
                    value: item.id,
                    label: s.label,
                  });
                }
              });
            });
            translatedServices.sort((a, b) => a.label.localeCompare(b.label));

            setServiceProvider({
              ...result.data.output,
              services: translatedServices,
            });
            setLoading(false);
          })
          .catch((err) => {
            setServiceProvider(null);
            setLoading(false);
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
              time: 7000,
            });
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [addToast, intl, loading, provisionTypes, schema, token]
  );

  const clearFormErrors = (ref) => {
    ref.current.setErrors({});
  };

  const handleSubmitAssociationForm = useCallback(
    async (data) => {
      schemaAssociationForm.current?.setErrors({});

      await schemaAssociationForm.validate(data, { abortEarly: false });
      setAssociateModalOpen(true);
    },
    [schemaAssociationForm]
  );

  const createAssociation = () => {
    try {
      handleLoading(true);
      api
        .post(
          "/provider-association",
          {
            eventId: event,
            opportunityId: contract,
            serviceProviderId: serviceProvider.id,
            serviceTypeId: service,
            allowManageAccount:
              formRef.current.getFieldValue("allowManageAccount"),
          },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((result) => {
          setAssociateModalOpen(false);
          addToast({
            type: "success",
            title: intl.formatMessage({
              id: "success",
            }),
            description: intl.formatMessage({
              id: "success.create_association",
            }),
          });
          history.push("/serviceprovider-association");
        })
        .finally(() => {
          handleLoading(false);
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
          setAssociateModalOpen(false);
        });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);

        formRef.current?.setErrors(errors);
        setAssociateModalOpen(false);

        return;
      }
    }
  };

  const getEvents = useCallback(() => {
    handleLoading(true);
    api
      .get("/provider-association/events", {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        const events = result.data.output.map((event) => ({
          label: event.eventEditionName,
          value: event.idNumber,
        }));
        let eventsSort = [...events].sort((a, b) =>
          a.label < b.label ? -1 : a.label > b.label ? 1 : 0
        );
        setAllEvents(eventsSort);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        handleLoading(false);
      });
  }, [addToast, intl, token, handleLoading]);

  const getOpportunities = useCallback(
    (event) => {
      handleLoading(true);
      api
        .get(`/provider-association/opportunities/${event}`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((result) => {
          setOpportunities(
            result.data.output.map((x) => ({ label: x.name, value: x.id }))
          );
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
        })
        .finally(() => {
          handleLoading(false);
        });
    },
    [addToast, intl, token, handleLoading]
  );

  useEffect(() => {
    getEvents();
  }, [getEvents]);

  useEffect(() => {
    if (event) getOpportunities(event);
  }, [event, getOpportunities]);

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();

    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const validationBlurAssociationForm = (name) => {
    let errors = associationFormRef.current.getErrors();

    validateSingleFieldOnBlur(
      errors,
      schemaAssociationForm,
      name,
      associationFormRef
    );
  };

  return (
    <>
      <AnimatedMainContainer
        breadcrumb={intl.formatMessage({ id: "breadcrumb.association" })}
      >
        <Container>
          <FormContainer>
            <TitleContainer>
              <Title>
                <FormattedMessage id="service_provider_association" />
              </Title>
              <GoBackContainer>
                <LinkButton
                  onClick={() => history.push("/serviceprovider-association")}
                >
                  <Badge
                    color="#002244"
                    text={intl.formatMessage({ id: "goback" })}
                    fontSize="12px"
                  />
                </LinkButton>
              </GoBackContainer>
            </TitleContainer>
            <FieldsContainer>
              <Form
                ref={formRef}
                onSubmit={handleSubmit}
                initialData={{
                  type: {
                    label: intl.formatMessage({ id: "all" }),
                    value: "ALL",
                  },
                  status: {
                    label: intl.formatMessage({ id: "both" }),
                    value: null,
                  },
                }}
              >
                <Toggle
                  name="allowManageAccount"
                  label={intl.formatMessage({ id: "allow_manage_account" })}
                />
                <Horizontal>
                  <SelectWithLabel
                    name="event"
                    options={allEvents}
                    validationBlur={validationBlur}
                    label={intl.formatMessage({ id: "event_edition" })}
                    placeholder={intl.formatMessage({ id: "event_edition" })}
                    onChange={(e) => setEvent(e.value)}
                    required
                  />

                  <SelectWithLabel
                    name="contract"
                    options={opportunities}
                    validationBlur={validationBlur}
                    label={intl.formatMessage({ id: "contract" })}
                    placeholder={intl.formatMessage({ id: "contract" })}
                    onChange={(e) => setContract(e.value)}
                    required
                  />
                </Horizontal>
                <Horizontal>
                  <CpfCnpj
                    name="document"
                    validationBlur={validationBlur}
                    placeholder={intl.formatMessage({
                      id: "document_cnpj_or_passport",
                    })}
                    label={intl.formatMessage({ id: "document_cnpj_or_passport" })}
                    hasLabel
                    required
                  />
                  <BadgeContainer>
                    <Badge
                      color="#002244"
                      text={intl.formatMessage({ id: "search" })}
                      onClick={() => formRef?.current?.submitForm()}
                      style={{ cursor: "pointer" }}
                    />
                    <UserNameContainer>
                      {serviceProvider ? (
                        serviceProvider?.name
                      ) : (
                        <div>{intl.formatMessage({ id: "search_user" })}</div>
                      )}
                    </UserNameContainer>
                  </BadgeContainer>
                </Horizontal>
              </Form>

              <Form
                ref={associationFormRef}
                onSubmit={handleSubmitAssociationForm}
              >
                <Horizontal>
                  <SelectWithLabel
                    name="service_type"
                    options={serviceProvider?.services}
                    validationBlur={validationBlurAssociationForm}
                    label={intl.formatMessage({ id: "service_type" })}
                    placeholder={intl.formatMessage({ id: "service_type" })}
                    onChange={(e) => setService(e.value)}
                    disabled={!serviceProvider}
                  />
                  <BadgeContainer>
                    <Badge
                      color="#002244"
                      text={intl.formatMessage({ id: "associate" })}
                      onClick={() => {
                        if (!service) {
                          return;
                        }
                        associationFormRef.current.submitForm();
                      }}
                      style={
                        service ? { cursor: "pointer" } : { cursor: "default" }
                      }
                      disabled={!service}
                    />
                  </BadgeContainer>
                </Horizontal>
              </Form>
            </FieldsContainer>
          </FormContainer>
        </Container>
      </AnimatedMainContainer>
      <ModalWithCheckbox
        modalIsOpen={associateModalOpen}
        setIsOpen={setAssociateModalOpen}
        confirmationFunction={createAssociation}
        description={intl.formatMessage({
          id: "associate_service_provider_modal_message",
        })}
        confirmationText={intl.formatMessage({ id: "confirm" })}
        cancelText={intl.formatMessage({ id: "cancel" })}
      />
    </>
  );
};

export default CreateAssociation;
